import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { I18NextPipe } from 'angular-i18next';
import { NGXLogger } from 'ngx-logger';
import { Store } from '@ngrx/store';
import { State } from 'src/app/dashboards/state';
import * as mainActions from 'src/app/state/app.actions';
import { ErrorMessage, InfoMessage } from 'src/app/common/messages';
import { DialogHelpComponent } from 'src/app/common/dialogs/dialog-help/dialog-help.component';
import { IHelpDialogData } from 'src/app/models';
import { realTimeService } from 'src/app/services/services/realtime.service';
import { RealtimeWallboardViewModel, WallboardData, QueueData, AgentData, ColumnData, ImpersonateUser, WallboardItem } from './models';
import { t } from 'i18next';
import { max } from 'moment';
import { WallboardSettingsComponent } from './settings/wallboard-settings.component';
import { trigger, state, style, transition, animate } from '@angular/animations';

@Component({
    selector: 'app-wallboard',
    templateUrl: './wallboard.component.html',
    styleUrls: ['./wallboard.component.scss'],
    animations: [
        trigger('slideInFromRight', [
            transition(':enter', [
                style({ transform: 'translateX(100%)' }),
                animate('0.3s ease-out', style({ transform: 'translateX(0)' }))
            ]),
            transition(':leave', [
                animate('0.3s ease-in', style({ transform: 'translateX(100%)' }))
            ])
        ])
    ],
    standalone: false
})
export class WallboardComponent implements OnInit, OnDestroy {

  // Component state
  isLoading = false;
  isFullscreen = false;
  showHelp = false;
  showSettings = false;
  errorMessage = '';

  // Dynamic font sizing for fullscreen
  firstColumnFontSize = '16px';
  otherColumnsFontSize = '16px';
  headerFontSize = '14px';

  // Cell padding constants
  private readonly NORMAL_CELL_PADDING = { top: 12, bottom: 12, left: 16, right: 16 };
  private readonly FULLSCREEN_CELL_PADDING = { top: 5, bottom: 5, left: 5, right: 5 };

  // Wallboard configuration
  wallboardType: 'Queue' | 'Agent' | 'Combined' | '' ;
  wallboardTypeCombinedState: 'Queue' | 'Agent' = 'Queue';
  wallboardResponseType: 'Queue' | 'Agent' = 'Queue';
  textColumns: string[] = ["wallboardAgentColumnHeader_UserGroup","wallboardAgentColumnHeader_AgentStatus","wallboardAgentColumnHeader_PhoneNumber","wallboardAgentColumnHeader_ProfileName"];
  displaySettingsButton = true;
  enableImpersonation = true;
  impersonationActive = false;
  ajaxRefreshInterval = 10000; // 10 seconds
  showClock = false;
  switchTime = 30; // 30 seconds
  serverTime = '';

  // Data
  wallboardData: WallboardData | null = null;
  displayQueues: QueueData[] = [];
  displayAgents: AgentData[] = [];
  displayColumns: string[] = [];
  displayItems: WallboardItem[] = []; // Unified list for both queues and agents

  // Impersonation
  impersonateUsers: ImpersonateUser[] = [];
  selectedImpersonateUser: number | null = null;

  // Refresh timer
  private refreshTimer: any;
  private switchTimer: any;
  private chunkTimer: any;

  // Reusable DOM element for HTML entity decoding
  private decodeTextarea: HTMLTextAreaElement | null = null;

  // Breadcrumbs
  BreadcrumbsLink = [
    { "label": "Home", "url": "/" },
    { "label": "Real-Time", "url": "/real-time/overview" },
    { "label": "Wallboard", "url": "/real-time/wallboard" }];
  maxRow: number;
  queuesChunkIndex: number;
  agentsChunkIndex: any;

  constructor(
    private router: Router,
    private dialog: MatDialog,
    private i18nextPipe: I18NextPipe,
    private logger: NGXLogger,
    private store: Store<State>,
    private realTimeService: realTimeService
  ) {
  }

  getWallboardDisplayType(): string {
   return this.wallboardType == "Queue" || (this.wallboardType == "Combined" && this.wallboardTypeCombinedState == "Queue") ? "Queue" : "Agent";
  }

  async ngOnInit(): Promise<void> {
    this.loadInitialData();
  }

  ngOnDestroy(): void {
    this.stopRefreshTimer();
    this.stopSwitchTimer();
    // Clean up reusable textarea element
    if (this.decodeTextarea && this.decodeTextarea.parentNode) {
      this.decodeTextarea.parentNode.removeChild(this.decodeTextarea);
    }
    this.decodeTextarea = null;
  }

  @HostListener('window:resize', ['$event'])
  onWindowResize(event: any): void {
    // if (this.isFullscreen) {
    //   setTimeout(() => this.calculateOptimalFontSizes2(), 300);
    // }
  }

  private loadInitialData(): void {
    this.stopRefreshTimer();
    this.stopSwitchTimer();
    this.stopChunkTimer();
    this.realTimeService.getWallboardProperties().subscribe({
      next: (response: RealtimeWallboardViewModel) => {
        this.impersonateUsers = response.impersonateUsers;
        this.enableImpersonation = response.enableImpersonation;
        this.wallboardType = response.wallboardType;
        this.displaySettingsButton = response.displaySettingsButton;
        this.ajaxRefreshInterval = (response.ajaxRefreshInterval ?? 10) * 1000;
        this.switchTime = response.switchTime;
        this.showClock = response.showClock;
        this.impersonationActive = response.impersonationActive;
        this.selectedImpersonateUser = response.impersonationUser;
        this.loadWallboardData(true);
        this.wallboardType ||= 'Queue';
        if (this.wallboardType === 'Combined') {
          this.wallboardTypeCombinedState = 'Queue';
          this.startCombinedTimer();
        } else {
          this.startRefreshTimer();
        }
      },
      error: (error) => {
        this.logger.error('Error loading impersonation users:', error);
        this.impersonateUsers = [];
      }
    });
  }

  private removePageBreaks(input: any): string {
      return input.replace(/<br\s*\/?>/gi, ' ');
  }

  private loadWallboardData(initialLoad: boolean = false): void {
    this.errorMessage = '';
    let wallboardType = this.wallboardType;
    if (this.wallboardType === 'Combined') {
      wallboardType = this.wallboardTypeCombinedState;
    }
    this.realTimeService.getWallboardData(wallboardType, true).subscribe({
      next: (response: WallboardData) => {
        if (initialLoad && this.wallboardType !== 'Combined') {
          this.startChunkTimer();
        }
        this.wallboardData = response;
        const settings = this.wallboardData.settings;
        this.maxRow = settings.maxRow > 25 ? 25 : settings.maxRow; // max 25 rows
        this.wallboardResponseType = this.wallboardData.wallboardType === 'Queue' ? 'Queue' : 'Agent';
        switch (this.wallboardResponseType) {
          case 'Queue':
            this.displayQueues = this.wallboardData.queues || [];
            this.displayColumns = (this.wallboardData.columns || []).map(col => this.removePageBreaks(this.i18nextPipe.transform(`realtime:${col}`)));
            this.displayAgents = [];
            if (initialLoad) {
              this.fillDisplayItems(initialLoad);
            }
            break;
          case 'Agent':
            this.displayAgents = this.wallboardData.agents || [];
            this.displayColumns = (this.wallboardData.agentColumns || []).map(col => this.removePageBreaks(this.i18nextPipe.transform(`realtime:${col}`)));

            if (this.wallboardData?.agentColumns?.some(col => col == "wallboardAgentColumnHeader_AgentStatus")) {
              const statusColIndex = this.wallboardData.agentColumns.findIndex(col => col == "wallboardAgentColumnHeader_AgentStatus");
              const statusTranslationMap: Record<string, string> = {
                'Ready': 'realtime:status_available',
                'Busy': 'realtime:status_busy',
                'Connected': 'realtime:status_connected',
                'Logged off': 'realtime:status_loggedOff',
                'No answer': 'realtime:status_noAnswer',
                'Pause': 'realtime:status_pause',
                'Wrap-up': 'realtime:status_wrapUp',
                'Connecting': 'realtime:status_connecting',
              };
              this.displayAgents.forEach(agent => {
                const rawStatus = String(agent.values[statusColIndex]);
                const translationKey = statusTranslationMap[rawStatus];
                if (translationKey) {
                  agent.values[statusColIndex] = this.i18nextPipe.transform(translationKey);
                }
              });
            }

            this.displayQueues = [];
            if (initialLoad) {
              this.fillDisplayItems(initialLoad);
            }
            break;
        }
      },
      error: (error) => {
        this.logger.error('Error loading wallboard data:', error);
        this.errorMessage = 'Failed to load wallboard data';
        this.isLoading = false;
      }
    });
  }

  public paginationText(): string {
    const totalItems = (this.wallboardType == 'Queue' || (this.wallboardType == 'Combined' && this.wallboardTypeCombinedState == 'Queue')) ? this.displayQueues.length : this.displayAgents.length;
    const startItem = ((this.wallboardType == 'Queue' || (this.wallboardType == 'Combined' && this.wallboardTypeCombinedState == 'Queue')) ? this.queuesChunkIndex : this.agentsChunkIndex) + 1;
    const page = Math.floor(startItem / this.maxRow) + 1;
    const totalPages = Math.ceil(totalItems / this.maxRow);
    if (totalPages <= 1 || isNaN(totalPages) || isNaN(page)) {
      return '';
    }
    return `${this.i18nextPipe.transform('Page')} ${page} ${this.i18nextPipe.transform('of')} ${totalPages}`;
  }

  private fillDisplayItems(init: boolean = false): void {
    if(this.wallboardType == null || this.wallboardType =='') {
      this.displayItems = [];
    };

if(this.wallboardResponseType=='Queue' ){
        this.queuesChunkIndex = (this.queuesChunkIndex || 0) + this.maxRow;
      if (this.queuesChunkIndex >= this.displayQueues.length || init) {
        this.queuesChunkIndex = 0;
      }
      const displayQueues = this.displayQueues.slice(this.queuesChunkIndex, this.queuesChunkIndex + this.maxRow);
      this.displayItems = this.createUnifiedList(displayQueues, []);
}else if(this.wallboardResponseType=='Agent' ){
       this.agentsChunkIndex = (this.agentsChunkIndex || 0) + this.maxRow;
      if (this.agentsChunkIndex >= this.displayAgents.length || init) {
        this.agentsChunkIndex = 0;
      }
      const displayAgents = this.displayAgents.slice(this.agentsChunkIndex, this.agentsChunkIndex + this.maxRow);
      this.displayItems = this.createUnifiedList([], displayAgents);
}
  }

  private startRefreshTimer(): void {
    this.stopRefreshTimer();
    this.refreshTimer = setInterval(() => {
      this.loadWallboardData();
    }, this.ajaxRefreshInterval);
  }

  private startSwitchTimer(): void {
    this.stopSwitchTimer();
    this.switchTimer = setInterval(() => {
      this.wallboardTypeCombinedState = this.wallboardTypeCombinedState === 'Queue' ? 'Agent' : 'Queue';
      this.loadWallboardData(true);
    }, this.switchTime * 1000);
  }

  private startChunkTimer(): void {
    this.stopChunkTimer();
    this.chunkTimer = setInterval(() => {
      this.fillDisplayItems();
    }, this.switchTime * 1000);
  }

  private startCombinedTimer(): void {
    this.stopChunkTimer();
    this.stopSwitchTimer();
    this.chunkTimer = setInterval(() => {
      this.onCombinedTick();
    }, this.switchTime * 1000);
  }

  private onCombinedTick(): void {
    const isQueue = this.wallboardTypeCombinedState === 'Queue';
    const items = isQueue ? this.displayQueues : this.displayAgents;
    const currentIndex = isQueue ? (this.queuesChunkIndex || 0) : (this.agentsChunkIndex || 0);
    const nextIndex = currentIndex + this.maxRow;

    if (nextIndex >= items.length) {
      this.wallboardTypeCombinedState = isQueue ? 'Agent' : 'Queue';
      this.loadWallboardData(true);
    } else {
      this.fillDisplayItems();
    }
  }

  private stopRefreshTimer(): void {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  private stopSwitchTimer(): void {
    if (this.switchTimer) {
      clearInterval(this.switchTimer);
      this.switchTimer = null;
    }
  }

  private stopChunkTimer(): void {
    if (this.chunkTimer) {
      clearInterval(this.chunkTimer);
      this.chunkTimer = null;
    }
  }

  toggleFullscreen(): void {
    this.isFullscreen = !this.isFullscreen;
  }

  goToSettings(): void {
    this.showSettings = true;
  }

  closeSettings(): void {
    this.showSettings = false;
  }

  onSettingsSaved(): void {
    // Reload wallboard data when settings are saved
    this.loadInitialData();
  }

  openHelp(): void {
    const data: IHelpDialogData = {
      title: this.i18nextPipe.transform('realtime:help_title_wallboard'),
      text: this.i18nextPipe.transform('realtime:help_wallboard'),
      renderAsHtml: true
    };

    this.dialog.open(DialogHelpComponent, {
      panelClass: 'dialog-help-panel',
      backdropClass: 'backdrop-invisible',
      data,
      width: '600px'
    });
  }

  switchImpersonation(userId: any): void {
    if (userId) {
      this.impersonationActive = true;
      this.selectedImpersonateUser = userId;
      this.startImpersonation();
    }
  }

  startImpersonation(): void {
    if (this.selectedImpersonateUser) {
      this.impersonationActive = true;
      sessionStorage.setItem("puzzel_editAsUserId", this.selectedImpersonateUser.toString());
      this.loadInitialData();
      this.logger.debug(`Started impersonation for user: ${this.selectedImpersonateUser}`);
    }
  }

  cancelImpersonation(): void {
    if (!this.impersonationActive) return;
    this.impersonationActive = false;
    this.selectedImpersonateUser = null;
    sessionStorage.removeItem("puzzel_editAsUserId");
    this.loadInitialData();
    this.addInfoMessage('Impersonation cancelled');
    this.logger.debug('Impersonation cancelled');
  }

  refreshWallboard(): void {
    // this.loadWallboardData();
    if (this.isFullscreen) {
      this.isFullscreen = false;
    }
  }

  // Method to manually trigger font size recalculation (for debugging)
  recalculateFontSizes(): void {
    // if (this.isFullscreen) {
    //   this.calculateOptimalFontSizes2();
    // }
  }

  hasAlarm(queue: QueueData): boolean {
    // Check if any value has red color (alarm condition)
    return queue.valueColors && queue.valueColors.some(color => color === '1');
  }

  private createUnifiedList(queues: QueueData[], agents: AgentData[]): WallboardItem[] {
    const items: WallboardItem[] = [];

    // Add queue items
    queues.forEach(queue => {
      items.push({
        type: 'queue',
        name: queue.queuename,
        values: queue.values || [],
        valueColors: queue.valueColors,
        isAlarm: this.hasAlarm(queue)
      });
    });

    // Add agent items
    agents.forEach(agent => {
      items.push({
        type: 'agent',
        name: agent.agentName,
        values: agent.values || [],
        isAlarm: false // Agents don't have alarm functionality
      });
    });

    return items;
  }

  getColumnWidth(): number {
    if (this.displayColumns.length === 0) return 100;
    return 75 / this.displayColumns.length; // 75% divided by number of columns
  }

  // Message handling
  private addErrorMessage(message: string): void {
    this.store.dispatch(
      mainActions.addMessage({
        message: new ErrorMessage(this.i18nextPipe.transform(`messages:${message}`), '')
      })
    );
  }

  private addInfoMessage(message: string): void {
    this.store.dispatch(
      mainActions.addMessage({
        message: new InfoMessage(this.i18nextPipe.transform(`messages:${message}`), '')
      })
    );
  }

  isSingleItemView(): boolean {
    if (this.displayItems.length === 1 && this.displayColumns.length <= 6 && (this.wallboardType == 'Queue' || this.wallboardTypeCombinedState == 'Queue')) return true;
    return false;
  }

  /**
   * Safely truncates a string that may contain HTML entities and Unicode characters
   * @param text The text to truncate (may contain HTML entities like &#248;)
   * @param maxLength Maximum length in characters (default: 25)
   * @returns Truncated text with ellipsis if needed
   */
  truncateText(text: string, maxLength: number = 25): string {
    if (!text) return '';

    // Decode HTML entities to get the actual text length
    const decodedText = this.decodeHtmlEntities(text);

    // Check if truncation is needed based on decoded text length
    if (decodedText.length <= maxLength) {
      return text; // Return original to preserve HTML entities
    }

    // Truncate the decoded text
    const truncatedDecoded = decodedText.substring(0, maxLength) + '...';

    // Escape HTML characters in the truncated text for safety when using innerHTML
    return this.escapeHtml(truncatedDecoded);
  }

  /**
   * Decodes HTML entities in a string
   * @param html The HTML string with entities
   * @returns Decoded plain text
   */
  private decodeHtmlEntities(html: string): string {
    // Create reusable textarea element lazily
    if (!this.decodeTextarea) {
      this.decodeTextarea = document.createElement('textarea');
      // Hide the element but keep it in the DOM for reuse
      this.decodeTextarea.style.position = 'absolute';
      this.decodeTextarea.style.left = '-9999px';
      this.decodeTextarea.style.top = '-9999px';
      document.body.appendChild(this.decodeTextarea);
    }
    this.decodeTextarea.innerHTML = html;
    return this.decodeTextarea.value;
  }

  /**
   * Escapes HTML characters to prevent XSS when using innerHTML
   * @param text The text to escape
   * @returns Escaped HTML string
   */
  private escapeHtml(text: string): string {
    const map: { [key: string]: string } = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;'
    };
    return text.replace(/[&<>"']/g, (m) => map[m]);
  }


  /* --------------------------------------------------------------------------------------------- */

  private calculateOptimalFontSizes2(): void {
    //return;
    if (!this.isFullscreen) return;
    const table = document.getElementById('wallboardTable');
    if (!table) return;

    const container = table.closest('.wallboard-container');
    if (!container) return;

    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;
    const headerHeight = 80; // Increased for header
    const footerHeight = 80; // Increased for footer
    const availableHeight = containerHeight - headerHeight - footerHeight;

    const numRows = Math.max(this.displayItems.length, 1);
    const numColumns = this.displayColumns.length + 1; // +1 for first column
    if (numRows === 0 || numColumns === 0) return;

    // Create test span for measuring text
    const testSpan = document.createElement('span');
    testSpan.style.visibility = 'hidden';
    testSpan.style.whiteSpace = 'nowrap';
    testSpan.style.position = 'absolute';
    //testSpan.style.padding = '10px 10px';
    testSpan.style.top = '-9999px';
    testSpan.style.fontWeight = '600';
    testSpan.style.fontFamily = this.wallboardData?.settings?.fontFamily || 'Arial, sans-serif';
    document.body.appendChild(testSpan);

    try {
      // Get all cells from the table
      const allCells = table.querySelectorAll('td, th');

      // --- Step 1: Calculate font size for header row ---
      const headerCells = table.querySelectorAll('thead th');
      if (headerCells.length > 0) {
        let headerMaxWidth = 0;
        let headerLongestText = '';
        let headerMaxHeight = 0;
        let headerHeightText = '';
        let headerCellWidth = (containerWidth * 0.75 / this.displayColumns.length) - 20;// 75% divided by number of columns - padding

        testSpan.style.fontWeight = '700';

        this.displayColumns.forEach(col => {
          testSpan.innerHTML = col;
          const width = testSpan.offsetWidth;
          const height = testSpan.offsetHeight;
          if (width > headerMaxWidth) {
            headerMaxWidth = width;
            headerLongestText = col;
          }
          if (height > headerMaxHeight) {
            headerMaxHeight = height;
            headerHeightText = col;
          }
        });
        if (this.isSingleItemView()) {
          headerLongestText = this.displayItems[0].name;
          headerHeightText = this.displayItems[0].name;
          headerCellWidth = (containerWidth * 0.75) - 20;
        }

        // Calculate optimal header font size
        let headerFontSize = 10;
        testSpan.innerHTML = headerLongestText;
        while (testSpan.offsetWidth < headerCellWidth && headerFontSize < 70) {
          headerFontSize++;
          testSpan.style.fontSize = headerFontSize + 'px';
        }

        headerFontSize -= 2;

        this.headerFontSize = `${Math.max(10, headerFontSize)}px`;
      }

      // --- Step 2: Calculate font size for first column ---
      if (this.displayItems.length > 0) {
        let firstColumnMaxWidth = 0;
        let firstColumnLongestText = '';
        let firstColumnMaxHeight = 0;
        let firstColumnHeightText = '';

        testSpan.style.fontSize = 10 + 'px';
        testSpan.style.fontWeight = '700';
        this.displayItems.forEach(item => {
          testSpan.innerHTML = item.name;
          const width = testSpan.offsetWidth;
          const height = testSpan.offsetHeight;
          if (width > firstColumnMaxWidth) {
            firstColumnMaxWidth = width;
            firstColumnLongestText = item.name;
          }
          if (height > firstColumnMaxHeight) {
            firstColumnMaxHeight = height;
            firstColumnHeightText = item.name;
          }
        });

        // Calculate optimal first column font size
        const firstColumnCellWidth = containerWidth * 0.25 - 20; // 25% of container width - padding
        const firstColumnCellHeight = availableHeight / numRows;
        let firstColumnFontSize = 5;

        testSpan.textContent = firstColumnLongestText;

        while (testSpan.offsetWidth < firstColumnCellWidth && testSpan.offsetHeight < firstColumnCellHeight && firstColumnFontSize < 200) {
          firstColumnFontSize++;
          testSpan.style.fontSize = firstColumnFontSize + 'px';
        }
        //firstColumnFontSize -= 2;

        this.firstColumnFontSize = `${Math.max(8, firstColumnFontSize)}px`;
      }

      // --- Step 3: Calculate font size for other columns ---
      if (this.isSingleItemView()) {
        if (this.displayColumns.length > 0) {
          let otherColumnsMaxWidth = 0;
          let otherColumnsLongestText = '';
          let otherColumnsMaxHeight = 0;
          let otherColumnsHeightText = '';

          testSpan.style.fontSize = 10 + 'px';
          testSpan.style.fontWeight = '600';


          for (const [index, col] of this.displayColumns.entries()) {
            let value = this.displayItems[0].values[index];
            testSpan.innerHTML = `${col}<br /><br />${value || '-'}`;
            const width = testSpan.offsetWidth;
            const height = testSpan.offsetHeight;
            if (width > otherColumnsMaxWidth) {
              otherColumnsMaxWidth = width;
              otherColumnsLongestText = col;
            }
            if (height > otherColumnsMaxHeight) {
              otherColumnsMaxHeight = height;
              otherColumnsHeightText = `${col}<br /><br />${value || '-'}`;
            }
          }
          const otherColumnCellWidth = (containerWidth * 0.75 / 3) - 20;
          const otherColumnCellHeight = (availableHeight / 2) - 20;


          let otherColumnsFontSizeWidth= 10;
          let otherColumnsFontSizeHeight = 10;

          testSpan.innerHTML = otherColumnsLongestText;
          while (testSpan.offsetWidth < otherColumnCellWidth && otherColumnsFontSizeWidth < 200) {
            otherColumnsFontSizeWidth++;
            testSpan.style.fontSize = otherColumnsFontSizeWidth + 'px';
          }
          otherColumnsFontSizeWidth -= 2;

          testSpan.innerHTML = otherColumnsHeightText;
          while (testSpan.offsetHeight < otherColumnCellHeight && otherColumnsFontSizeHeight < 200) {
            otherColumnsFontSizeHeight++;
            testSpan.style.fontSize = otherColumnsFontSizeHeight + 'px';
          }
          otherColumnsFontSizeHeight -= 2;

          this.otherColumnsFontSize = `${Math.max(10, Math.min(otherColumnsFontSizeWidth, otherColumnsFontSizeHeight))}px`;
        }

      } else {
        if (this.displayColumns.length > 0) {
          let otherColumnsMaxWidth = 0;
          let otherColumnsLongestText = '';

          testSpan.style.fontSize = 10 + 'px';
          testSpan.style.fontWeight = '600';

          this.displayItems.map(item => { return item.values }).flat().forEach(val => {
            testSpan.innerHTML = val?.toString() || '';
            const width = testSpan.offsetWidth;
            if (width > otherColumnsMaxWidth) {
              otherColumnsMaxWidth = width;
              otherColumnsLongestText = val?.toString() || '';
            }
          });

          const otherColumnCellWidth = (containerWidth * 0.75 / this.displayColumns.length) - 10; // 75% divided by number of columns - padding
          const otherColumnCellHeight = (availableHeight / numRows) - 10; // Row height - padding


          let otherColumnsFontSize = 10;

          testSpan.innerHTML = otherColumnsLongestText;

          while (testSpan.offsetWidth < otherColumnCellWidth && testSpan.offsetHeight < otherColumnCellHeight && otherColumnsFontSize < 200) {
            otherColumnsFontSize++;
            testSpan.style.fontSize = otherColumnsFontSize + 'px';
          }
          otherColumnsFontSize -= 1;

          this.otherColumnsFontSize = `${Math.max(10, otherColumnsFontSize)}px`;
        }
      }


    } finally {
      // Clean up test span
      if (document.body.contains(testSpan)) {
        document.body.removeChild(testSpan);
      }
    }
  }




  /* --------------------------------------------------------------------------------------------- */


  private calculateOptimalFontSizes(): void {
    if (!this.isFullscreen) return;

    const table = document.getElementById('wallboardTable');
    if (!table) return;

    const container = table.closest('.wallboard-container');
    if (!container) return;

    // Get available space
    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;
    const headerHeight = 80; // Increased for header
    const footerHeight = 0; // Increased for footer
    const availableHeight = containerHeight - headerHeight - footerHeight;

    // Calculate optimal font sizes based on content and available space
    const numRows = Math.max(this.displayItems.length, 1);
    const numColumns = this.displayColumns.length + 1; // +1 for first column

    if (numRows === 0 || numColumns === 0) return;

    const firstColumn = document.getElementsByClassName('first-column')?.item(0);
    const valueColumns = document.getElementsByClassName('value-column')?.item(0);

    const cellHeight = firstColumn?.clientHeight || (availableHeight / numRows);

    // Calculate font sizes - first column typically needs more space for names
    const firstColumnWidth = firstColumn?.clientWidth || 0;
    const otherColumnWidth = valueColumns?.clientWidth || 0;

    // Get current cell padding based on fullscreen mode
    const cellPadding = this.getCurrentCellPadding();

    // Calculate available space after accounting for padding
    const availableFirstColumnWidth = Math.max(0, firstColumnWidth - cellPadding.left - cellPadding.right);
    const availableOtherColumnWidth = Math.max(0, otherColumnWidth - cellPadding.left - cellPadding.right);
    const availableCellHeight = Math.max(0, cellHeight - cellPadding.top - cellPadding.bottom);

    // Measure text content length to adjust font size accordingly
    const textMetrics = this.measureTextContent();

    // Calculate text-based font size adjustments using available space (after padding)
    const firstColumnTextAdjustment = this.calculateTextBasedFontAdjustment(
      textMetrics.maxFirstNameLength,
      availableFirstColumnWidth,
      availableCellHeight
    );

    const otherColumnsTextAdjustment = this.calculateTextBasedFontAdjustment(
      textMetrics.maxValueLength,
      availableOtherColumnWidth,
      availableCellHeight
    );

    // Calculate header font size - ignore height, only consider width and text content
    const headerTextAdjustment = this.calculateHeaderTextBasedFontAdjustment(
      textMetrics.maxHeaderLength,
      availableOtherColumnWidth
    );

    // More aggressive font size calculation for fullscreen with text size consideration
    // Use available space after padding for more accurate calculations
    const firstColumnFontSize = Math.min(
      Math.max(availableCellHeight * 0.6, 16), // Minimum 16px, max 60% of available cell height
      Math.max(availableFirstColumnWidth * 0.25, 16), // Also consider available width
      firstColumnTextAdjustment // Apply text-based adjustment
    );

    const otherColumnsFontSize = Math.min(
      Math.max(availableCellHeight * 0.5, 14), // Slightly smaller than first column
      Math.max(availableOtherColumnWidth * 0.3, 14), // Also consider available width
      otherColumnsTextAdjustment // Apply text-based adjustment
    );

    // Calculate header font size - ignore height, only consider width and text content
    const headerFontSize = Math.min(
      Math.max(availableOtherColumnWidth * 0.25, 12), // Consider available width
      headerTextAdjustment, // Apply text-based adjustment
      otherColumnsFontSize * 0.95 // Cap at 95% of other columns font size
    );

    // Apply the calculated font sizes with more aggressive sizing
    this.firstColumnFontSize = `${Math.round(firstColumnFontSize)}px`;
    this.otherColumnsFontSize = `${Math.round(otherColumnsFontSize)}px`;
    this.headerFontSize = `${Math.round(headerFontSize)}px`;
  }

  /**
   * Measures the text content length for both first column names and value columns
   * @returns Object containing maximum text lengths for font size calculation
   */
  private measureTextContent(): { maxFirstNameLength: number; maxValueLength: number; maxHeaderLength: number } {
    let maxFirstNameLength = 0;
    let maxValueLength = 0;
    let maxHeaderLength = 0;

    // Measure first column names (queue names or agent names)
    this.displayItems.forEach(item => {
      if (item.name) {
        const nameLength = this.getTextLength(item.name);
        maxFirstNameLength = Math.max(maxFirstNameLength, nameLength);
      }
    });

    // Measure value column content
    this.displayItems.forEach(item => {
      if (item.values) {
        item.values.forEach(value => {
          if (value !== null && value !== undefined) {
            const valueLength = this.getTextLength(value.toString());
            maxValueLength = Math.max(maxValueLength, valueLength);
          }
        });
      }
    });

    // Measure header column content
    this.displayColumns.forEach(column => {
      if (column) {
        const columnTranslate = this.i18nextPipe.transform(`realtime:${column}`);
        const headerLength = this.getTextLength(columnTranslate);
        maxHeaderLength = Math.max(maxHeaderLength, headerLength);
      }
    });

    return {
      maxFirstNameLength,
      maxValueLength,
      maxHeaderLength
    };
  }

  /**
   * Gets the effective text length considering character width variations
   * @param text The text to measure
   * @returns Effective text length for font size calculation
   */
  private getTextLength(text: string): number {
    if (!text) return 0;
    const split = text.toLocaleLowerCase().split(/<br\s*\/?>/);
    text = '';
    split.forEach((part) => {
      if (part.length > text.length) text = part;
    });
    // Remove HTML tags for accurate length measurement
    const cleanText = text.replace(/<[^>]*>/g, '');

    // Calculate effective length considering character width variations
    // Wide characters (like W, M) count more than narrow ones (like i, l)
    let effectiveLength = 0;
    for (const char of cleanText) {
      if (this.isWideCharacter(char)) {
        effectiveLength += 1.5; // Wide characters take more space
      } else if (this.isNarrowCharacter(char)) {
        effectiveLength += 0.7; // Narrow characters take less space
      } else {
        effectiveLength += 1; // Normal characters
      }
    }

    return effectiveLength;
  }

  /**
   * Checks if a character is considered wide (takes more horizontal space)
   * @param char The character to check
   * @returns True if the character is wide
   */
  private isWideCharacter(char: string): boolean {
    const wideChars = 'WQMWQmwq@#$%&*()_+{}|:"<>?[]\\;\'.,/ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    return wideChars.includes(char);
  }

  /**
   * Checks if a character is considered narrow (takes less horizontal space)
   * @param char The character to check
   * @returns True if the character is narrow
   */
  private isNarrowCharacter(char: string): boolean {
    const narrowChars = 'il|!jftr';
    return narrowChars.includes(char);
  }

  /**
   * Gets the current cell padding based on fullscreen mode
   * @returns Current cell padding configuration
   */
  private getCurrentCellPadding(): { top: number; bottom: number; left: number; right: number } {
    return this.isFullscreen ? this.FULLSCREEN_CELL_PADDING : this.NORMAL_CELL_PADDING;
  }

  /**
   * Calculates font size adjustment for headers based on text content length (ignores height)
   * @param textLength The effective text length
   * @param availableWidth The available width for the text
   * @returns Recommended font size based on text content
   */
  private calculateHeaderTextBasedFontAdjustment(
    textLength: number,
    availableWidth: number
  ): number {
    if (textLength === 0 || availableWidth === 0) {
      return 12; // Default header font size
    }

    // Calculate optimal font size based on text fitting in available width only
    // Width-based calculation: estimate characters per line
    const estimatedCharsPerLine = Math.floor(availableWidth / 8); // Rough estimate: 8px per character
    const linesNeeded = Math.ceil(textLength / estimatedCharsPerLine);

    // Calculate font size based on width constraints only
    const maxFontSizeByWidth = (availableWidth / textLength) * 2; // Rough scaling factor

    // Apply additional scaling based on text length
    // Longer text gets proportionally smaller font size
    const lengthScalingFactor = Math.max(0.5, 1 - (textLength - 10) * 0.02); // Scale down for very long text
    const adjustedFontSize = maxFontSizeByWidth * lengthScalingFactor;

    // Ensure reasonable bounds for headers
    return Math.max(8, Math.min(adjustedFontSize, 36)); // Between 8px and 36px for headers
  }

  /**
   * Calculates font size adjustment based on text content length
   * @param textLength The effective text length
   * @param availableWidth The available width for the text
   * @param availableHeight The available height for the text
   * @returns Recommended font size based on text content
   */
  private calculateTextBasedFontAdjustment(
    textLength: number,
    availableWidth: number,
    availableHeight: number
  ): number {
    if (textLength === 0 || availableWidth === 0 || availableHeight === 0) {
      return 16; // Default font size
    }

    // Calculate optimal font size based on text fitting in available space
    // This is a heuristic that considers both width and height constraints

    // Width-based calculation: estimate characters per line
    const estimatedCharsPerLine = Math.floor(availableWidth / 8); // Rough estimate: 8px per character
    const linesNeeded = Math.ceil(textLength / estimatedCharsPerLine);

    // Height-based calculation: ensure text fits in available height
    const maxFontSizeByHeight = availableHeight * 0.8; // Use 80% of available height
    const maxFontSizeByWidth = (availableWidth / textLength) * 2; // Rough scaling factor

    // Take the more restrictive constraint
    const textBasedFontSize = Math.min(maxFontSizeByHeight, maxFontSizeByWidth);

    // Apply additional scaling based on text length
    // Longer text gets proportionally smaller font size
    const lengthScalingFactor = Math.max(0.5, 1 - (textLength - 10) * 0.02); // Scale down for very long text
    const adjustedFontSize = textBasedFontSize * lengthScalingFactor;

    // Ensure reasonable bounds
    return Math.max(8, Math.min(adjustedFontSize, 48)); // Between 8px and 48px
  }
}
